import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { EventEmitter, Injectable, Injector } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
// import { ImageCompressService, IImage } from 'ng2-image-compress';
import Compressor from 'compressorjs';

import { AppSettingsService } from './app-settings.service';
import { ImpersonationService } from './shared/impersonation.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class HttpService {


  private RESOURCE_API_BASE_URL = this.appSettings.resourceUrl;

  constructor(
    private http: HttpClient,
    private appSettings: AppSettingsService,
    private storageService: StorageService,
    private injector: Injector
    // private imgCompressService: ImageCompressService
  ) { }

  private catchErrorWithExtraData(error: any): HttpErrorResponse {
    let updateError: HttpErrorResponse = error;
    try {
      updateError = {...error, error: JSON.parse(error.error.message)};
    } catch { }
    return updateError;
  }


  public get(url: string) {
    const headers = this.generateDefaultHeaders();
    return this.http.get(this.getFullUrl(url), {
      headers,
      withCredentials: true
    });
  }

  public getTyped<T>(url: string, queryParams?: HttpParams) {
    return of({}).pipe(
      switchMap(() => {
        return this.http.get<T>(this.getFullUrl(url), {
          headers: this.generateDefaultHeaders(),
          params: queryParams,
          withCredentials: true
        });
      }),
      catchError((error: HttpErrorResponse) => {
        throw this.catchErrorWithExtraData(error);
      })
    );
  }

  public post(url: string, data: any, options?: CustomHttpOptions) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const requestBody = data ? JSON.stringify(data) : null;

    if (options && options.observe !== 'body') {
      return this.http.post(this.getFullUrl(url), requestBody, {
        observe: 'response',
        withCredentials: true,
        headers
      });
    } else {
      return this.http.post(this.getFullUrl(url), requestBody, {
        withCredentials: true,
        headers
      });
    }
  }

  public postTyped<T>(url: string, data: any) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const requestBody = data ? JSON.stringify(data) : null;
    return this.http.post<T>(this.getFullUrl(url), requestBody, {
      withCredentials: true,
      headers
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        throw this.catchErrorWithExtraData(error);
      })
    );
  }

  public put(url: string, data: any) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const requestBody = data ? JSON.stringify(data) : null;
    return this.http.put(this.getFullUrl(url), requestBody, {
      withCredentials: true,
      headers
    });
  }

  public putTyped<T>(url: string, data: any = null) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const requestBody = data ? JSON.stringify(data) : null;
    return this.http.put<T>(this.getFullUrl(url), requestBody, {
      withCredentials: true,
      headers
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        throw this.catchErrorWithExtraData(error);
      })
    );
  }

  public patchTyped<T>(url: string, data: any) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');
    const requestBody = data ? JSON.stringify(data) : null;
    return this.http.patch<T>(this.getFullUrl(url), requestBody, {
      withCredentials: true,
      headers
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        throw this.catchErrorWithExtraData(error);
      })
    );
  }

  public deleteTyped<T>(url: string) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Content-Type', 'application/json');

    return this.http.delete<T>(this.getFullUrl(url), {
      withCredentials: true,
      headers
    }).pipe(
      catchError((error: HttpErrorResponse) => {
        throw this.catchErrorWithExtraData(error);
      })
    );
  }

  // public compressImage(file: File): Observable<File> {
  //   let images: Array<IImage> = [];
  //   images.push({
  //     fileName: file.name;
  //     imageObjectUrl: file.;
  //     imageDataUrl: string;
  //     type: string;
  //     compressedImage: IImage;
  //   });
  //   ImageCompressService.filesArrayToCompressedImageSource(File)
  // }

  public compressImage(file: File): Observable<File> {
    // return of(file);
    const emitter = new EventEmitter<File>();
    // if the file is not an images, return in as is:

    const fileName = file.name.toLowerCase();

    if (!fileName.endsWith('.png') && !fileName.endsWith('.jpg') && !fileName.endsWith('.jpeg') && !fileName.endsWith('.bmp')) {
      return of(file);
    } else {
      // Compress the image:
      // https://www.npmjs.com/package/compressorjs

      const compressor = new Compressor(file, {
        quality: 0.6,
        maxWidth: 1920,
        success(blobResult) {
          const compressedFile = new File([blobResult], file.name, {type: file.type, lastModified: Date.now()});
          emitter.emit(compressedFile);
        },
        error(err) {
          emitter.error(err);
          console.log(err.message);
        }
      });
    }

    return emitter.asObservable();
  }

  public uploadFile(url: string, file: File) {
    const formData: FormData = new FormData();
    formData.append(file.name, file);
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Accept', 'application/json');
    const req = new HttpRequest('POST', this.getFullUrl(url), formData, {
      headers,
      withCredentials: true,
      reportProgress: true
    });
    return this.http.request(req);
  }

  public downloadFile(url: string, fileType: string) {
    let headers = this.generateDefaultHeaders();
    headers = headers.append('Accept', fileType);
    return this.http.get(this.getFullUrl(url), {
      headers,
      withCredentials: true,
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      switchMap(resp => {
        const downloadLink = document.createElement('a');
        const blob = new Blob([resp.body], {type: fileType});
        // for some reason, sometimes the character " is appended before and after the filename
        // if it happens - we're removing it in the next if statement
        let fileName = 'file';
        if (!!resp.headers.get('content-disposition') && resp.headers.get('content-disposition')?.split('filename=').length >= 2) {
          const contentDispositionParts = resp.headers.get('content-disposition').split(';').map(part => part.toLowerCase().trim())
          fileName = contentDispositionParts.filter(part => part.startsWith('filename='))[0].substring('filename='.length);
          if (fileName.charAt(0) === '"' && fileName.charAt(fileName.length - 1) === '"') {
            fileName = fileName.substring(1, fileName.length - 1);
          }
        }

        downloadLink.href = window.URL.createObjectURL(blob);
        downloadLink.setAttribute('download', fileName);
        downloadLink.click();
        return of(true);
      })
    );
  }

  public delete(url: string) {
    const headers = this.generateDefaultHeaders();
    return this.http.delete(this.getFullUrl(url), {headers, withCredentials: true});
  }

  public generateDefaultHeaders(): HttpHeaders {
    const authDataJson = this.storageService.getAuthorizationData();
    const impersonationService = this.injector.get<ImpersonationService>(ImpersonationService);
    let headers = new HttpHeaders();
    if (authDataJson && authDataJson.access_token) {
      headers = headers.append('Authorization', 'Bearer ' + authDataJson.access_token);
    }
    if (impersonationService.isViewingAsInvestor) {
      headers = headers.append('Impersonate-As', impersonationService.impersonateAsUsername);
    }

    return headers;
  }

  public getFullUrl(url: string): string {
    if (url.indexOf('http') === 0) {
      return url;
    }

    return this.RESOURCE_API_BASE_URL + url;
  }
}

export class CustomHttpOptions {
  public observe?: string;
}
